import { format, isValid } from 'date-fns';
import { ar, fr } from 'date-fns/locale';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { IngredientIcon } from '@/components/ui/ingredient-icon';
import { useInventoryValidations, TimeFilter } from '@/hooks/useInventoryValidations';
import {
  Loader2,
  TrendingDown,
  TrendingUp,
  Minus,
  CalendarIcon,
  ChevronLeft,
  ChevronRight,
  X,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { hasPermission } from '@/lib/permissions';
import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import { useAssistants } from '@/hooks/useAssistants';
import { DeleteValidationDialog } from './DeleteValidationDialog';
import type { InventoryValidation } from '@/types/entities';

interface ValidationHistoryTableProps {
  restaurantId: string;
  ingredientId?: string;
}

export function ValidationHistoryTable({
  restaurantId,
  ingredientId,
}: ValidationHistoryTableProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ar' ? ar : fr;
  const isRTL = i18n.language === 'ar';

  // Get assistant data for permission checks
  const { data: assistant } = useCurrentAssistant();
  const { data: allAssistants } = useAssistants(restaurantId);

  // Filter state
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [createdByFilter, setCreatedByFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Memoize filters to prevent infinite re-renders
  const filters = useMemo(() => {
    // If a specific date is selected, use it as both start and end date
    if (selectedDate) {
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);
      return {
        ingredientId,
        startDate: startOfDay,
        endDate: endOfDay,
        createdBy: createdByFilter ?? undefined,
        page,
        pageSize,
      };
    }
    return {
      ingredientId,
      timeFilter,
      createdBy: createdByFilter ?? undefined,
      page,
      pageSize,
    };
  }, [ingredientId, timeFilter, selectedDate, createdByFilter, page, pageSize]);

  const { data: response, isLoading } = useInventoryValidations(restaurantId, filters);

  const validations = response?.data || [];
  const totalPages = response?.totalPages || 1;
  const totalCount = response?.totalCount || 0;

  // State for delete dialog
  const [deletingValidation, setDeletingValidation] = useState<InventoryValidation | null>(null);

  // Check permissions - if assistant is logged in, check their permissions; otherwise assume admin (full access)
  const canDelete = assistant
    ? hasPermission(assistant.permissions, 'stock', 'delete_validation')
    : true;

  // Reset to page 1 when filters change
  const handleTimeFilterChange = (newFilter: TimeFilter) => {
    setTimeFilter(newFilter);
    setSelectedDate(undefined);
    setPage(1);
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setPage(1);
  };

  const clearDateFilter = () => {
    setSelectedDate(undefined);
    setPage(1);
  };

  // Get all possible creator names: "Admin" + all assistant usernames
  const createdByNames = useMemo(() => {
    const names: string[] = ['Admin'];
    if (allAssistants) {
      allAssistants.forEach((a) => {
        if (a.username && !names.includes(a.username)) {
          names.push(a.username);
        }
      });
    }
    return names;
  }, [allAssistants]);


  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Only count non-deleted validations in totals
  const totalShortage = validations
    .filter((v) => !v.deletedAt && (Number(v.costVariance) || 0) < 0)
    .reduce((sum, v) => sum + Math.abs(Number(v.costVariance) || 0), 0);

  const totalOverage = validations
    .filter((v) => !v.deletedAt && (Number(v.costVariance) || 0) > 0)
    .reduce((sum, v) => sum + (Number(v.costVariance) || 0), 0);

  const getDifferenceDisplay = (difference: number) => {
    const diff = Number(difference) || 0;
    if (diff === 0) {
      return {
        icon: Minus,
        color: 'text-blue-600',
        bg: 'bg-blue-100',
        label: t('inventory.validateStockDialog.exactMatch'),
      };
    } else if (diff < 0) {
      return {
        icon: TrendingDown,
        color: 'text-red-600',
        bg: 'bg-red-100',
        label: t('inventory.validateStockDialog.shortage'),
      };
    } else {
      return {
        icon: TrendingUp,
        color: 'text-green-600',
        bg: 'bg-green-100',
        label: t('inventory.validateStockDialog.overage'),
      };
    }
  };

  const getReasonLabel = (reason: string | null) => {
    const reasons: Record<string, string> = {
      periodic_count: t('inventory.validateStockDialog.reasonPeriodic'),
      discrepancy_check: t('inventory.validateStockDialog.reasonDiscrepancy'),
      year_end: t('inventory.validateStockDialog.reasonYearEnd'),
      other: t('inventory.validateStockDialog.reasonOther'),
    };
    return (
      reasons[reason || 'periodic_count'] ||
      reason ||
      t('inventory.validateStockDialog.reasonPeriodic')
    );
  };

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-col gap-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span>{t('inventory.validationHistory.title')}</span>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-red-50 border-red-200">
                {t('inventory.validationHistory.losses')}: {totalShortage.toFixed(2)}{' '}
                {t('common.currency')}
              </Badge>
              <Badge variant="outline" className="bg-green-50 border-green-200">
                {t('inventory.validationHistory.overage')}: {totalOverage.toFixed(2)}{' '}
                {t('common.currency')}
              </Badge>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Time Filter Buttons */}
            <div className="flex items-center gap-1 rounded-lg border p-1">
              <Button
                variant={timeFilter === 'today' && !selectedDate ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleTimeFilterChange('today')}
                className="h-7 px-2 text-xs"
              >
                {t('inventory.filters.today')}
              </Button>
              <Button
                variant={timeFilter === 'week' && !selectedDate ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleTimeFilterChange('week')}
                className="h-7 px-2 text-xs"
              >
                {t('inventory.filters.week')}
              </Button>
              <Button
                variant={timeFilter === 'month' && !selectedDate ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleTimeFilterChange('month')}
                className="h-7 px-2 text-xs"
              >
                {t('inventory.filters.month')}
              </Button>
              <Button
                variant={timeFilter === 'all' && !selectedDate ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleTimeFilterChange('all')}
                className="h-7 px-2 text-xs"
              >
                {t('inventory.filters.all')}
              </Button>
            </div>

            {/* Calendar Picker */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={selectedDate ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'h-8 gap-2 text-xs',
                    selectedDate && 'bg-primary text-primary-foreground'
                  )}
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {selectedDate
                    ? format(selectedDate, 'dd/MM/yyyy', { locale })
                    : t('inventory.filters.selectDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align={isRTL ? 'end' : 'start'}>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) {
                      const localDate = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate(),
                        0,
                        0,
                        0,
                        0
                      );
                      handleDateSelect(localDate);
                    } else {
                      handleDateSelect(undefined);
                    }
                  }}
                  initialFocus
                  locale={locale}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  weekStartsOn={isRTL ? 6 : 1}
                  disabled={(date) => date > new Date()}
                />
              </PopoverContent>
            </Popover>

            {/* Clear Date Filter */}
            {selectedDate && (
              <Button variant="ghost" size="sm" onClick={clearDateFilter} className="h-8 px-2">
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          {/* Created By Filter Badges */}
          {createdByNames.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">{t('inventory.filters.createdBy')}:</span>
              <Badge
                variant={createdByFilter === null ? 'default' : 'outline'}
                className="cursor-pointer text-xs"
                onClick={() => { setCreatedByFilter(null); setPage(1); }}
              >
                {t('inventory.filters.all')}
              </Badge>
              {createdByNames.map((name) => (
                <Badge
                  key={name}
                  variant={createdByFilter === name ? 'default' : 'outline'}
                  className="cursor-pointer text-xs"
                  onClick={() => { setCreatedByFilter(createdByFilter === name ? null : name); setPage(1); }}
                >
                  {name}
                </Badge>
              ))}
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {validations.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            {t('inventory.noValidations')}
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]"></TableHead>
                    <TableHead className="text-right">
                      {t('inventory.validationHistory.date')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.validationHistory.ingredient')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.validationHistory.calculated')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.validationHistory.actual')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.validationHistory.difference')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.validationHistory.costImpact')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.validationHistory.reason')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.validationHistory.createdBy')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('inventory.receiptHistory.deletedBy')}
                    </TableHead>
                    {canDelete && (
                      <TableHead className="text-right w-[100px]">
                        {t('common.actions')}
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {validations.map((validation) => {
                    const diffDisplay = getDifferenceDisplay(validation.difference);
                    const DiffIcon = diffDisplay.icon;
                    const validationDate = new Date(validation.validationDate);
                    const isDeleted = !!validation.deletedAt;
                    const updatedByDisplay = (validation as any).updated_by_display || (validation as any).updatedByDisplay;

                    return (
                      <React.Fragment key={validation.id}>
                        <TableRow className={cn(isDeleted && 'opacity-60 bg-muted/30')}>
                          <TableCell>
                            {isDeleted && (
                              <Badge variant="destructive" className="text-xs">
                                {t('inventory.validationHistory.deleted')}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className={cn('font-medium', isDeleted && 'line-through')}>
                            {isValid(validationDate)
                              ? format(validationDate, 'dd/MM/yyyy', { locale })
                              : '-'}
                          </TableCell>
                          <TableCell className={cn(isDeleted && 'line-through')}>
                            <div className="flex items-center gap-2">
                              <IngredientIcon ingredient={validation.ingredient || null} size={32} />
                              <span>
                                {i18n.language === 'ar'
                                  ? validation.ingredient?.nameAr
                                  : validation.ingredient?.nameFr}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className={cn(isDeleted && 'line-through')}>
                            {(Number(validation.calculatedQuantity) || 0).toFixed(3)}{' '}
                            {validation.unit}
                          </TableCell>
                          <TableCell className={cn(isDeleted && 'line-through')}>
                            {(Number(validation.actualQuantity) || 0).toFixed(3)} {validation.unit}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={cn(
                                `gap-1 ${diffDisplay.bg} ${diffDisplay.color} border-current`,
                                isDeleted && 'line-through'
                              )}
                            >
                              <DiffIcon className="h-3 w-3" />
                              {(Number(validation.difference) || 0) > 0 ? '+' : ''}
                              {(Number(validation.difference) || 0).toFixed(3)} {validation.unit}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span
                              className={cn(
                                `font-mono font-medium ${
                                  (Number(validation.costVariance) || 0) < 0
                                    ? 'text-red-600'
                                    : (Number(validation.costVariance) || 0) > 0
                                      ? 'text-green-600'
                                      : 'text-blue-600'
                                }`,
                                isDeleted && 'line-through'
                              )}
                            >
                              {(Number(validation.costVariance) || 0) > 0 ? '+' : ''}
                              {(Number(validation.costVariance) || 0).toFixed(2)}{' '}
                              {t('common.currency')}
                            </span>
                          </TableCell>
                          <TableCell className={cn('text-muted-foreground text-sm', isDeleted && 'line-through')}>
                            {getReasonLabel(validation.reason)}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {(validation as any).created_by_display || (validation as any).createdByDisplay || '-'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {isDeleted && updatedByDisplay ? (
                              <span className="text-xs text-muted-foreground italic">
                                {t('inventory.receiptHistory.deletedByLabel')}: {updatedByDisplay}
                              </span>
                            ) : (
                              <span>-</span>
                            )}
                          </TableCell>
                          {canDelete && (
                            <TableCell>
                              {isDeleted ? (
                                <span className="text-xs text-muted-foreground italic">
                                  {t('inventory.validationHistory.alreadyDeleted')}
                                </span>
                              ) : (
                                <div className="flex items-center gap-1 justify-end">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => setDeletingValidation(validation)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between mt-4 gap-3 pt-4 border-t">
                <div className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left order-2 sm:order-1">
                  {t('inventory.pagination.showing', {
                    from: (page - 1) * pageSize + 1,
                    to: Math.min(page * pageSize, totalCount),
                    total: totalCount,
                  })}
                </div>
                <div className="flex items-center gap-2 order-1 sm:order-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="h-9"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('inventory.pagination.previous')}</span>
                  </Button>
                  <span className="text-sm font-medium text-muted-foreground min-w-[60px] text-center">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="h-9"
                  >
                    <span className="hidden sm:inline">{t('inventory.pagination.next')}</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>

      {/* Delete Validation Dialog */}
      {deletingValidation && (
        <DeleteValidationDialog
          restaurantId={restaurantId}
          validation={deletingValidation}
          open={!!deletingValidation}
          onOpenChange={(open) => !open && setDeletingValidation(null)}
        />
      )}
    </>
  );
}
